import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import "@/assets/scss/index.css";
import VueFeather from "vue-feather";
import { VueQueryPlugin } from "@tanstack/vue-query";
import HSButton from "@/components/form/button/HSButton";
import HSInput from "@/components/form/inputs/HSInput";
import HSField from "@/components/form/inputs/HSField";
import SideBar from "@/components/sidebar/SideBar";
import StatsCard from "@/components/cards/StatsCard";
import StoreCard from "@/components/cards/StoreCard";
import SearchBar from "@/components/cards/SearchBar";
import SystemErrors from "@/components/dashboard/SystemErrors";
import HSChart from "@/components/dashboard/HSChart";
import UpdateProfile from "@/components/modals/profile/UpdateProfile";
import ShareDashboard from "@/components/modals/store/ShareDashboard";
import UpdateStore from "@/components/modals/store/UpdateStore";
import StoreItemSync from "@/components/modals/store/StoreItemSync";
import ModalView from "@/components/modals/ModalView";
import HSTable from "@/components/table/HSTable";
import HSDropdown from "@/components/form/dropdown/HSDropdown";
import Vue3Toasity from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import Permissions from "./helper/permissions";
import HSSelect from "./components/form/inputs/HSSelect";
import VueChartkick from "vue-chartkick";
import "chartkick/chart.js";
import DateFilter from "@/components/dashboard/DateFilter.vue";
import {
  formatDateTime,
  formatDate,
  capitalizeText,
  formatNumber,
  generateTableHeaderKeyMappingFromJson,
  generateTableHeadersFromJson,
  convertGMTToLocalTime,
  safeJSONParse,
} from "@/helper/utils";
import VCalendar from "v-calendar";
import { DatePicker } from "v-calendar";
import "v-calendar/style.css";
import { initializeApp, getApps } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getMessaging } from "firebase/messaging";
import StoreSelector from "@/components/dashboard/StoreSelector.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import HSTableSkeleton from "@/components/table/HSTableSkeleton.vue";
import HSTabs from "@/components/tabs/HSTabs.vue";
import HSPagination from "@/components/table/HSPagination.vue";
import HSAccordion from "./components/accordion/HSAccordion.vue";

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

// Check if all environment variables are defined
const areEnvVarsDefined = Object.values(firebaseConfig).every(
  (value) => value !== undefined
);

if (areEnvVarsDefined) {
  // Initialize Firebase only if all environment variables are defined
  let firebaseApp;
  if (getApps().length === 0) {
    firebaseApp = initializeApp(firebaseConfig);
  } else {
    firebaseApp = getApps()[0];
  }
  getAnalytics(firebaseApp);
  
  // Initialize messaging (only in browser, not in service worker)
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      getMessaging(firebaseApp);
    } catch (error) {
      console.warn('Firebase Messaging initialization failed:', error);
    }
  }
} else {
  console.warn(
    "Firebase environment variables are not fully defined. Firebase initialization skipped."
  );
}

const app = createApp(App);

app.component(VueFeather.name, VueFeather);
app.component("hs-button", HSButton);
app.component("hs-input", HSInput);
app.component("hs-field", HSField);
app.component("hs-table", HSTable);
app.component("hs-dropdown", HSDropdown);
app.component("side-bar", SideBar);
app.component("stats-card", StatsCard);
app.component("store-card", StoreCard);
app.component("search-bar", SearchBar);
app.component("system-errors", SystemErrors);
app.component("hs-chart", HSChart);
app.component("update-profile", UpdateProfile);
app.component("share-dashboard", ShareDashboard);
app.component("update-store", UpdateStore);
app.component("store-item-sync", StoreItemSync);
app.component("modal-view", ModalView);
app.component("hs-select", HSSelect);
app.component("date-filter", DateFilter);
app.component("store-selector", StoreSelector);
app.component("confirmation-modal", ConfirmationModal);
app.component("hs-table-skeleton", HSTableSkeleton);
app.component("hs-tabs", HSTabs);
app.component("hs-pagination", HSPagination);
app.component("hs-accordion", HSAccordion);
app.component("v-date-picker", DatePicker);

app.config.globalProperties.permissions = new Permissions([
  "SUPER_ADMIN",
  "AUDITOR",
]);

app.config.globalProperties.$formatDateTime = formatDateTime;
app.config.globalProperties.$formatDate = formatDate;
app.config.globalProperties.$capitalizeText = capitalizeText;
app.config.globalProperties.$formatNumber = formatNumber;
app.config.globalProperties.$generateTableHeaderKeyMappingFromJson =
  generateTableHeaderKeyMappingFromJson;
app.config.globalProperties.$generateTableHeadersFromJson =
  generateTableHeadersFromJson;
app.config.globalProperties.$convertGMTToLocalTime = convertGMTToLocalTime;
app.config.globalProperties.$safeJSONParse = safeJSONParse;

// Configure TanStack Query
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 10 * 60 * 1000, // 10 minutes default
        cacheTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  },
});

app.use(store);
app.use(router);
app.use(Vue3Toasity, {
  autoClose: 3000,
});
app.use(VCalendar, {});
app.use(VueChartkick);
app.mount("#app");
